import { useState } from "react";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, formatApiError, type BillingMe } from "@/lib/api";
import { formatDate, mxn } from "@/lib/billing";

export function GuaranteeBox({ b, onDone }: { b: BillingMe; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const g = b.guarantee;

  if (notice || g.status === "refund_failed") {
    return <p className="mt-4 rounded-lg bg-[#FFFBEB] px-3 py-2 text-sm text-amber-900" data-testid="guarantee-pending">{notice || "Recibimos tu solicitud; te contactaremos en menos de 24 horas."}</p>;
  }
  if (g.status === "refunded") {
    return <p className="mt-4 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-900" data-testid="guarantee-refunded">Usaste tu garantía: te devolvimos {mxn(g.amount || 0)}. El tiempo en que se refleja depende de tu banco.</p>;
  }
  if (!g.available) return null;

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post<{ status: string; message?: string }>("/billing/guarantee");
      if (data.status === "refunded") { toast.success("Tu reembolso está en camino"); onDone(); }
      else setNotice(data.message || "");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); setOpen(false); }
  };

  return (
    <div className="mt-4 flex flex-col gap-3 rounded-xl border border-[#C9A84C]/50 bg-amber-50/40 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="guarantee-box">
      <p className="text-sm text-slate-700"><ShieldCheck className="mr-1 inline h-4 w-4 text-[#C9A84C]" />Tienes hasta el <strong data-testid="guarantee-until">{formatDate(g.until)}</strong> para usar tu garantía de 7 días.</p>
      <Button variant="outline" onClick={() => setOpen(true)} className="h-10 shrink-0 border-[#1B2A4A] text-[#1B2A4A]" data-testid="guarantee-button">Usar mi garantía de 7 días</Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="guarantee-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Usar tu garantía?</AlertDialogTitle>
            <AlertDialogDescription data-testid="guarantee-dialog-text">
              Te devolvemos {mxn(g.amount || 0)} y tu suscripción se cancela hoy. Ya no tendrás acceso a la app, pero tu información queda guardada por si regresas. ¿Continuar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="guarantee-cancel">No, volver</AlertDialogCancel>
            <AlertDialogAction onClick={(e: React.MouseEvent) => { e.preventDefault(); confirm(); }} disabled={busy} className="bg-[#1B2A4A] text-white hover:bg-[#2A3D63]" data-testid="guarantee-confirm">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sí, reembolsar y cancelar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
